/** @jsxImportSource @emotion/react */
'use memo'

import { useState } from 'react'
import styled from '@emotion/styled'
import colors from '@sportninja/common/constants/appColors'
import type { OrgRegistration } from '@sportninja/common/features/organization/registration/types'
import { buildOrgRegistrationCardModel } from '@sportninja/common/features/organization/registration/utils/cardModel'
import OrgRegistrationActionStrip from './OrgRegistrationActionStrip'
import OrgRegistrationCardHeader from './OrgRegistrationCardHeader'
import OrgRegistrationDivisionGrid from './OrgRegistrationDivisionGrid'
import OrgRegistrationDivisionsToggle from './OrgRegistrationDivisionsToggle'

interface Props {
  registration: OrgRegistration
  /** The moment every card on the screen counts down from. */
  now: Date
  onOpen: (registration: OrgRegistration) => void
  onShare: (registration: OrgRegistration) => void
}

/**
 * One registration: identity over its sport's art, the countdown and actions,
 * and its divisions table behind a disclosure row. Cards expand
 * independently, so several tables can be open at once.
 */
const OrgRegistrationCard = ({ registration, now, onOpen, onShare }: Props) => {
  'use memo'

  const [isExpanded, setIsExpanded] = useState(false)
  const model = buildOrgRegistrationCardModel(registration, now)
  const divisionsId = `org-registration-${registration.id}-divisions`

  return (
    <Card>
      <OrgRegistrationCardHeader registration={registration} model={model} />
      <OrgRegistrationActionStrip
        model={model}
        onOpen={() => onOpen(registration)}
        onShare={() => onShare(registration)}
      />
      <OrgRegistrationDivisionsToggle
        divisionCount={registration.divisions.length}
        isExpanded={isExpanded}
        controlsId={divisionsId}
        onToggle={() => setIsExpanded((expanded) => !expanded)}
      />
      {/* The toggle offers no control without divisions, so this stays closed. */}
      {isExpanded && (
        <div id={divisionsId}>
          <OrgRegistrationDivisionGrid
            divisions={registration.divisions}
            inactiveAvailabilityLabel={model.inactiveAvailabilityLabel}
          />
        </div>
      )}
    </Card>
  )
}

export default OrgRegistrationCard

const Card = styled.article`
  background: ${colors.SECONDARY_350};
  border: 1px solid ${colors.SECONDARY_200};
  border-radius: 8px;
  overflow: hidden;
  transition: border-color 0.15s ease;

  &:hover {
    border-color: ${colors.SECONDARY_100};
  }
`
